<template>
  <div class="home">
    <nav class="nav">
      <div class="container">
        <div class="nav__list">
          <div class="nav__link">Bid</div>
          <span>/</span>
          <div class="nav__link nav__link--active">My Home</div>
        </div>
        <div class="nav__button">
          <a href="https://docs.gmsm.app" target="_blank">Docs</a>
        </div>
      </div>
    </nav>
    <main>
      <div class="container">
        <h1>My Home</h1>
        <div class="seller">
          <div class="seller__header">
            <div class="seller__title">Adapter Health <span>?</span></div>
            <div class="seller__dropdown">
              <span>Time Period:</span>
              <SelectField
                :value="timePeriod"
                @value-changed="onSelectFieldChange"
              />
            </div>
          </div>
          <div class="seller__body">
            <div class="seller__table">
              <div class="seller__table-header seller__table-row">
                <div class="seller__table-column seller__table-column--seller">
                  Seller:
                </div>
                <div class="seller__table-column seller__table-column--status">
                  Status:
                </div>
                <div class="seller__table-column seller__table-column--requests">
                  Requests:
                </div>
                <div class="seller__table-column seller__table-column--bids">
                  Bids:
                </div>
                <div class="seller__table-column seller__table-column--bid-rate">
                  Bid Rate:
                </div>
                <div class="seller__table-column seller__table-column--top-bid-rate">
                  Top Bid Rate:
                </div>
                <div class="seller__table-column seller__table-column--pass-rate">
                  Pass Rate:
                </div>
                <div class="seller__table-column seller__table-column--timeout-rate">
                  Timeout Rate:
                </div>
                <div class="seller__table-column seller__table-column--error-rate">
                  Error Rate:
                </div>
              </div>
              <div
                class="seller__table-row seller__table-row--values"
                v-for="seller in filteredSellers"
                :key="seller.seller"
                @click="selectSeller(seller.seller)"
              >
                <div class="seller__table-column seller__table-column--seller">
                  {{ seller.seller }}
                </div>
                <div
                  class="seller__table-column seller__table-column--status"
                  :class="{
                    'seller__table-column--status-green': !seller.status.error,
                    'seller__table-column--status-red': seller.status.error,
                  }"
                  :title="seller.status.error ? seller.status.message : ''"
                >
                  <span>{{ seller.status.message }}</span>
                </div>
                <div class="seller__table-column seller__table-column--requests">
                  {{ seller.requests | precisionNumber | formatCount }}
                </div>
                <div class="seller__table-column seller__table-column--bids">
                  {{ seller.bids | precisionNumber | formatCount }}
                </div>
                <div class="seller__table-column seller__table-column--bid-rate">
                  {{ seller.bid_rate | precisionNumber | percentNumber}} %
                </div>
                <div class="seller__table-column seller__table-column--top-bid-rate">
                  {{ seller.top_bid_rate | precisionNumber | percentNumber }}
                  {{ !seller.top_bid_rate || seller.top_bid_rate === null ? '-' : '%' }}
                </div>
                <div class="seller__table-column seller__table-column--pass-rate">
                  {{ seller.pass_rate | precisionNumber | percentNumber }} %
                </div>
                <div class="seller__table-column seller__table-column--timeout-rate">
                  {{ seller.timeout_rate | precisionNumber | percentNumber }} %
                </div>
                <div class="seller__table-column seller__table-column--error-rate">
                  {{ seller.error_rate | precisionNumber | percentNumber }} %
                </div>
              </div>
            </div>
          </div>
        </div>
        <Logo />
      </div>
    </main>
  </div>
</template>

<script>
import {
  startOfToday,
  subDays,
  isWithinInterval,
} from 'date-fns';
import { mapActions, mapGetters } from 'vuex';
import { types } from '../store';
// eslint-disable-next-line import/extensions
import SelectField, { VALUES } from '../components/common/SelectField';
// eslint-disable-next-line import/extensions
import Logo from '../components/Logo';
import { formatCount } from '../js/helpers/formatNumber';

const PERIOD_TIMESTAMPS = {
  [VALUES.yesterday]: subDays(startOfToday(), 1),
  [VALUES.week]: subDays(startOfToday(), 7),
  [VALUES.month]: subDays(startOfToday(), 30),
};
const KEYS_TO_BE_AVERAGE = ['bid_rate', 'top_bid_rate', 'pass_rate', 'timeout_rate', 'error_rate'];

export default {
  components: {
    SelectField,
    Logo,
  },
  filters: {
    formatCount,
    precisionNumber(value) {
      if (value) {
        const splitValue = String(value).split('.');
        const int = splitValue[0];
        const double = splitValue[1] ? splitValue[1].slice(0, 5) : '';
        return double ? `${int}.${double}` : `${int}`;
      }
      return value;
    },
    percentNumber(value) {
      if (value) {
        return (value * 100).toFixed(2);
      }
      return value;
    },
  },
  data: () => ({
    KEYS_TO_BE_AVERAGE,
    PERIOD_TIMESTAMPS,
    timePeriod: VALUES.week,
  }),
  computed: {
    ...mapGetters({
      sellers: types.GET_SELLERS,
      showLoader: types.GET_SHOW_LOADER,
    }),
    sellersInsideTimePeriod() {
      // safe clone array of obj
      const sellers = JSON.parse(JSON.stringify(this.sellers));
      return sellers
        // filter sellers depending on timePeriod (yesterday, 7 days, 30 days)
        .filter(
          item => isWithinInterval(
            new Date(item.date.value),
            {
              start: PERIOD_TIMESTAMPS[this.timePeriod],
              end: startOfToday(),
            },
          ),
        )
        // sort filtered sellers DESC
        .sort((a, b) => new Date(b.date.value).getTime() - new Date(a.date.value).getTime());
    },
    filteredSellers() {
      const sellersCount = this.sellersInsideTimePeriod
        .map(item => item.seller)
        .filter((value, index, self) => self.indexOf(value) === index).length;
      const uniqueItemsLength = this.sellersInsideTimePeriod.length / sellersCount;
      return this.sellersInsideTimePeriod
        // filter sorted sellers to show only lates by the date data
        .reduce((acc, curr) => {
          const sellerIndex = acc.findIndex(item => item.seller === curr.seller);
          if (sellerIndex === -1) {
            acc.push(curr);
          } else {
            Object.keys(acc[sellerIndex])
              .filter(key => typeof acc[sellerIndex][key] === 'number')
              .forEach((key) => {
                acc[sellerIndex][key] += curr[key];
              });
          }
          return acc;
        }, [])
        .map((item) => {
          const safeItem = item;
          KEYS_TO_BE_AVERAGE.forEach((key) => {
            safeItem[key] /= uniqueItemsLength;
          });
          return safeItem;
        });
    },
  },
  watch: {
    showLoader(newStatus) {
      document.documentElement.style.overflow = newStatus ? 'hidden' : 'auto';
    },
  },
  created() {
    if (!this.sellers.length) {
      this.fetchData();
    }
  },
  methods: {
    ...mapActions({
      fetchData: types.FETCH_OVERVIEW_STATS,
      setSelectedSeller: types.SET_SELECTED_SELLER,
    }),
    onSelectFieldChange(value) {
      this.timePeriod = value;
    },
    selectSeller(seller) {
      this.setSelectedSeller(seller);
      this.$router.push('/seller');
    },
  },
};
</script>

<style lang="scss" scoped>
main {
  background-color: #F1F2F4;
  min-height: calc(100vh - 50px);
  padding-bottom: 32px;

  h1 {
    padding: 24px 0;
    color: #0A2756;
    font-size: 22px;
    font-weight: 500;
    line-height: 27px;
  }
}
.container {
  width: 100%;
  max-width: 1240px;
  padding: 0 20px;
  margin: 0 auto;
}
.nav {
  padding: 16px 0;
  box-shadow: 0 4px 12px 0 #E3E6EA;
  height: 50px;
  box-sizing: border-box;
}
.nav__list {
  display: flex;

  span {
    color: #99A5B7;
  }
}
.nav__link {
  margin: 0 8px;
  color: #0080FF;
  font-size: 14px;
  line-height: 16px;

  &:first-child {
    margin-left: 0;
  }

  &--active {
    color: #213B65;
  }
}
.nav__button {
  margin: -15px 20px;
  font-size: 12px;
  color: #0080FF;
  float: right;
  padding: 4px;
  border-radius: 6px;
  background-color: rgba(0, 128, 255, 0.2);

  a {
    text-decoration: none;
  }
}
.seller {
  border-radius: 2px;
  background-color: #FFFFFF;
  box-shadow: 0 4px 12px 0 #E3E6EA;
}
.seller__header {
  padding: 16px 24px;
  border-bottom: 1px solid #E8EBEF;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.seller__dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  line-height: 16px;

  span {
    margin-right: 8px;
  }
}
.seller__title {
  color: #0A2756;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.21px;
  line-height: 19px;

  span {
    border-radius: 50%;
    height: 18px;
    width: 18px;
    background-color: rgba(#0081C6, 0.4);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    color: #0081C6;
    font-size: 18px;
    margin-left: 4px;
  }
}
.seller__body {
  padding: 24px;
}
.seller__table-column {
  color: #0A2756;
  font-size: 14px;
  line-height: 16px;

  .seller__table-header & {
    font-weight: 500;
  }

  .seller__table-row--values &.seller__table-column--domain {
    color: #0080FF;
  }

  &:not(:last-child) {
    border-right: 1px solid #E3E6EA;
  }
}
.seller__table {
  border: 1px solid #E3E6EA;
  border-radius: 2px;
}
.seller__table-row {
  display: flex;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid #E3E6EA;
  }
}
.seller__table-header {
  background-color: #F1F2F4;
}
.seller__table-column {
  padding: 12px 16px;
  flex: none;
}
.seller__table-column--status {
  span {
    color: #fff;
    text-transform: uppercase;
    width: 47px;
    text-align: center;
    display: inline-block;
    font-size: 10px;
    font-weight: 500;
    line-height: 12px;
    padding: 3px 4px;
  }
  &-green span { background-color: #87CB9C; }
  &-red span { background-color: #F47F64; }
}
.seller__table-column--seller {
  width: 17%;
  transition: .3s ease-out;

  .seller__table-row:hover & {
    color: #0080FF;
  }
}
.seller__table-column--status {width: 5%;}
.seller__table-column--requests {width: 6%;}
.seller__table-column--bids {width: 5%;}
.seller__table-column--bid-rate {width: 7%;}
.seller__table-column--top-bid-rate {width: 10%;}
.seller__table-column--pass-rate {width: 8%;}
.seller__table-column--timeout-rate {width: 10%;}
.seller__table-column--error-rate {width: 8%;}
</style>
